<template>
    <div class="rounded-2xl border border-valence-primary-200 bg-valence-primary-100 p-4 md:p-6 flex flex-col gap-4">
        <!-- Header with icon and person's name -->
        <div class="flex flex-col gap-4">
            <div class="flex items-start gap-2 md:gap-3">
                <div class="flex h-8 w-8 md:h-10 md:w-10 items-center justify-center rounded-full bg-white text-valence-primary-400 shadow-sm shrink-0">
                    <i class="bi text-base md:text-lg" :class="COLLABORATION_PROFILE_BOOTSTRAP_ICON_CLASS" aria-hidden="true"></i>
                </div>
                <div class="flex flex-col gap-1 min-w-0">
                    <div class="flex items-center gap-2 flex-wrap">
                        <h3 class="body-small-semibold md:body-semibold text-valence-grey-900">{{ displayName }}'s Collaboration Profile</h3>
                        <StatusChip v-if="isInactive" state="neutral" size="xs">Inactive</StatusChip>
                    </div>
                    <p class="details md:body-small text-valence-grey-750">How {{ firstName }} likes to work and collaborate with others.</p>
                </div>
            </div>
        </div>

        <BaseTabs v-model="activeProfileTab" :tab-ids="profileTabIds" hide-single-tab-list class="gap-4">
            <template #trigger-working> Working with me </template>
            <template v-if="isManager" #trigger-team> How my team should work </template>
            <template #trigger-basic> Basic information </template>
            <template #content-basic>
                <BasicInfoCard :basic-info="basicInfo" :hris-data="hrisData" :facet-fields="facetFields">
                    <template v-if="$slots['extra-fields']" #extra-fields="slotProps">
                        <slot name="extra-fields" v-bind="slotProps" />
                    </template>
                </BasicInfoCard>
            </template>
            <template #content-working>
                <ProfileTopicSection
                    :is-loading="isProfileTopicsLoading"
                    :error="profileTopicsErrorMessage"
                    :is-enabled="publicProfileEnabled"
                    :has-profile="hasPublicProfile"
                    :entries="workingWithMeEntries"
                    :disabled-state="profileDisabledState"
                    :empty-profile-state="workingEmptyProfileState"
                    :empty-entries-state="workingEmptyEntriesState" />
            </template>
            <template #content-team>
                <ProfileTopicSection
                    :is-loading="isProfileTopicsLoading"
                    :error="profileTopicsErrorMessage"
                    :is-enabled="publicProfileEnabled"
                    :has-profile="hasPublicProfile"
                    :entries="teamWorkEntries"
                    :disabled-state="profileDisabledState"
                    :empty-profile-state="teamEmptyProfileState"
                    :empty-entries-state="teamEmptyEntriesState" />
            </template>
        </BaseTabs>
    </div>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { HrisData } from "~vue/appTypes";
import BaseTabs from "~vue/components/BaseTabs.vue";
import BasicInfoCard from "~vue/components/BasicInfoCard.vue";
import type { ProfileEntry } from "~vue/components/ProfileTopicSection.vue";
import ProfileTopicSection from "~vue/components/ProfileTopicSection.vue";
import StatusChip from "~vue/components/StatusChip.vue";
import type { FacetValue } from "~vue/types/connection";
import { toProfileEntry } from "~vue/utils/profileEntries";
import { computed, ref, watch } from "vue";

import { COLLABORATION_PROFILE_BOOTSTRAP_ICON_CLASS } from "./navigation/menuPanes";

const props = withDefaults(
    defineProps<{
        targetUserId: string | null;
        basicInfo: {
            full_name: string;
            role: string | null;
            team: string | null;
            level: string | null;
            organization: string | null;
        };
        publicProfileEnabled: boolean;
        hrisData?: HrisData | null;
        facetFields?: FacetValue[];
        isInactive?: boolean;
    }>(),
    {
        targetUserId: null,
        publicProfileEnabled: false,
        hrisData: null,
        facetFields: () => [],
        isInactive: false,
    },
);

const displayName = computed(() => props.basicInfo?.full_name || "Unknown");
const firstName = computed(() => displayName.value.split(" ")[0] || displayName.value);

enum ProfileTabId {
    Basic = "basic",
    WorkingWithMe = "working",
    TeamWork = "team",
}

const activeProfileTab = ref<ProfileTabId>(ProfileTabId.WorkingWithMe);

const profileTabIds = computed(() => {
    const ids = [ProfileTabId.WorkingWithMe];
    if (isManager.value) {
        ids.push(ProfileTabId.TeamWork);
    }
    ids.push(ProfileTabId.Basic);
    return ids;
});

interface PropertySubtopic {
    id: string;
    title: string;
    placeholder: string;
    order: number;
    current?: {
        content: string;
        access: string;
    };
}

interface PropertyTopic {
    id: string;
    topic: string;
    placeholder: string;
    managers_only?: boolean;
    subtopics: PropertySubtopic[];
}

interface PropertyTopicsResponse {
    topics: PropertyTopic[];
}

const {
    data: profileTopics,
    isFetching: isProfileTopicsLoading,
    error: profileTopicsError,
    execute: fetchProfileTopics,
    statusCode: profileTopicsStatusCode,
} = useFetch(() => (props.targetUserId ? `/api/accounts/profile/topics/${props.targetUserId}/` : ""), { immediate: false, refetch: true }).json<PropertyTopicsResponse>();

const hasPublicProfile = computed(() => {
    if (isProfileTopicsLoading.value) {
        return true;
    }
    if (profileTopicsError.value && profileTopicsStatusCode.value == 404) {
        // Treat the "not found" error case as the target user not having a public profile.  This
        // can happen if the target user deleted their public profile, or if they moved to a
        // different org from the current user.
        return false;
    }
    return true;
});

const profileTopicsErrorMessage = computed(() => {
    if (!profileTopicsError.value) {
        return null;
    }
    if (profileTopicsStatusCode.value == 404) {
        // We handle the not-found case as hasPublicProfile = false
        return null;
    }
    if (typeof profileTopicsError.value === "string") {
        return profileTopicsError.value;
    }
    return profileTopicsError.value.message || "Unable to load profile";
});

const shouldFetchProfileTopics = computed(() => props.publicProfileEnabled && props.targetUserId !== null);

watch(
    shouldFetchProfileTopics,
    async (shouldFetch) => {
        if (shouldFetch) {
            await fetchProfileTopics();
        }
    },
    { immediate: true },
);

const profileTopicMap = computed(() => {
    const topics = profileTopics.value?.topics ?? [];
    return new Map(topics.map((topic) => [topic.topic, topic]));
});

function buildProfileEntries(topicName: string): ProfileEntry[] {
    const topic = profileTopicMap.value.get(topicName);
    if (!topic) {
        return [];
    }

    return topic.subtopics
        .filter((subtopic) => Boolean(subtopic.current?.content?.trim()))
        .map((subtopic) =>
            toProfileEntry({
                id: subtopic.id,
                title: subtopic.title,
                content: subtopic.current?.content ?? "",
                placeholder: subtopic.placeholder,
            }),
        );
}

const workingWithMeEntries = computed(() => buildProfileEntries("How to Work With Me"));
const teamWorkEntries = computed(() => buildProfileEntries("How My Team Should Work"));

const profileDisabledState = {
    title: "Collaboration profiles are not enabled",
    body: "Collaboration profile sharing is disabled for your account.",
    icon: "bi-shield-lock",
};

const workingEmptyProfileState = {
    title: "No collaboration profile available",
    body: "This person hasn't shared their collaboration profile yet.",
    icon: "bi-person-lines-fill",
};

const workingEmptyEntriesState = {
    title: "No responses yet",
    body: "This person hasn't added details about how they like to work with others.",
    icon: "bi-chat-left-text",
};

const teamEmptyProfileState = {
    title: "No collaboration profile available",
    body: "This person hasn't shared their collaboration profile yet.",
    icon: "bi-people",
};

const teamEmptyEntriesState = {
    title: "No responses yet",
    body: "This person hasn't added details about how they want their team to collaborate.",
    icon: "bi-kanban",
};

const isManager = computed(() => {
    // Check if any topic has managers_only flag set to true
    const topics = profileTopics.value?.topics ?? [];
    return topics.some((topic) => topic.managers_only === true);
});
</script>
